import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { BombiixModal } from '../BombiixModal';

/**
 * Confirmation modal for supplier uploads where existing records are detected.
 * Update re-triggers the Lambda via the /confirmed/ S3 path (full upsert); close discards the upload.
 * @param {object} props - Component props.
 * @param {boolean} props.show - Whether the modal is open.
 * @param {number} props.duplicateCount - Number of existing records found; 0 when unknown.
 * @param {Function} props.onAccept - Confirms the upsert.
 * @param {Function} props.onCancel - Discards the upload.
 * @returns {JSX.Element} The duplicate-records confirmation.
 */
function SupplierDuplicateConfirmModal({ show, duplicateCount, onAccept, onCancel }) {
  const { t } = useTranslation('modules/partner-overview');
  const { t: tCommon } = useTranslation('common');

  const handleAccept = useCallback(() => {
    onAccept();
  }, [onAccept]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const message =
    duplicateCount > 0 ? t('SUPPLIER_OVERVIEW.DUPLICATE_MODAL_MESSAGE_COUNT', { count: duplicateCount }) : t('SUPPLIER_OVERVIEW.DUPLICATE_MODAL_MESSAGE');

  return (
    <BombiixModal
      show={show}
      size="md"
      title={t('SUPPLIER_OVERVIEW.DUPLICATE_MODAL_TITLE')}
      onClose={handleCancel}
      onSave={handleAccept}
      saveLabel={tCommon('COMMON_LABEL.UPDATE')}
    >
      <div className="bombiix-modal__confirm-body">
        <p className="mb-0 text-center">{message}</p>
      </div>
    </BombiixModal>
  );
}

SupplierDuplicateConfirmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  duplicateCount: PropTypes.number,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SupplierDuplicateConfirmModal.defaultProps = {
  duplicateCount: 0,
};

export default SupplierDuplicateConfirmModal;
